import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, DollarSign, ChevronDown, ChevronRight, Calendar, Send, CheckCircle, AlertCircle, Landmark } from 'lucide-react';
import { balanceConsolidadoService, BalanceEntidad } from '../../services/balanceConsolidadoService';
import { deudaService } from '../../services/deudaService';
import { notificacionBalanceService } from '../../services/notificacionBalanceService';
import { NotificacionBalance } from '../../lib/supabase';
import { BalanceResumen } from '../../services/balanceService';
import { OwnerContext } from '../../types/actividad';
import { MESES, formatCurrency } from '../../utils/formatters';
import { useToggleSet } from '../../hooks/useToggleSet';
import { PillSelect } from '../ui/PillSelect';
import { NotificarBalanceModal } from '../NotificarBalanceModal';
import { FormErrorAlert } from '../ui/FormErrorAlert';
import { reportarErrorInesperado } from '../../utils/reportarErrorInesperado';
import { FondosCampanaTabla } from '../FondosCampanaTabla';
import { useAuth } from '../../context/AuthContext';
import { puedeOperarDinero } from '../../utils/permissions';

interface BalanceJAViewProps {
  readonly ownerContext: OwnerContext;
  readonly modo?: 'propio' | 'agrupado';
  readonly initialMes?: number;
  readonly initialAño?: number;
}

// ---------------------------------------------------------------------------
// Helpers de etiquetas
// ---------------------------------------------------------------------------

function resolverEtiquetaJA(tipo: OwnerContext['tipo']): string {
  if (tipo === 'diocesis') return 'JAD';
  return 'JAN';
}

function resolverTituloColumna(tipo: OwnerContext['tipo']): string {
  if (tipo === 'global') return 'Provincia Eclesiástica';
  return 'Diócesis';
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6 animate-pulse">
    <div className="h-4 bg-slate-200 rounded w-32 mb-3" />
    <div className="h-8 bg-slate-200 rounded w-40" />
  </div>
);

const SkeletonRow: React.FC = () => (
  <tr className="border-t border-slate-100 animate-pulse" aria-hidden="true" tabIndex={-1}>
    <td className="px-6 py-4"><div className="h-4 bg-slate-200 rounded w-48" /></td>
    <td className="px-6 py-4"><div className="h-4 bg-slate-200 rounded w-24 ml-auto" /></td>
    <td className="px-6 py-4"><div className="h-4 bg-slate-200 rounded w-24 ml-auto" /></td>
    <td className="px-6 py-4"><div className="h-4 bg-slate-200 rounded w-24 ml-auto" /></td>
  </tr>
);

const SkeletonTabla: React.FC = () => (
  <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
    <div className="p-6 border-b border-slate-200">
      <div className="h-6 bg-slate-200 rounded w-32 animate-pulse" />
    </div>
    <table className="w-full">
      <tbody>
        {Array.from({ length: 4 }, (_, i) => <SkeletonRow key={`skel-row-${i}`} />)}
      </tbody>
    </table>
  </div>
);

const SkeletonCards: React.FC = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    {Array.from({ length: 3 }, (_, i) => <SkeletonCard key={`skel-card-${i}`} />)}
  </div>
);

// ---------------------------------------------------------------------------
// Tarjetas de resumen (modo propio)
// ---------------------------------------------------------------------------

interface ResumenCardsProps {
  readonly balance: BalanceResumen;
  readonly etiqueta: string;
}

const ResumenCards: React.FC<ResumenCardsProps> = ({ balance, etiqueta }) => (
  <div className={`grid grid-cols-1 gap-4 ${balance.financiamiento > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-5 h-5 text-emerald-600" />
        <span className="text-sm font-medium text-slate-600">Total Ingresos</span>
      </div>
      <p className="text-2xl font-bold text-slate-800">{formatCurrency(balance.ingresos)}</p>
    </div>

    <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-2">
        <TrendingDown className="w-5 h-5 text-red-600" />
        <span className="text-sm font-medium text-slate-600">Total Costos</span>
      </div>
      <p className="text-2xl font-bold text-slate-800">{formatCurrency(balance.egresos)}</p>
    </div>

    {balance.financiamiento > 0 && (
      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Landmark className="w-5 h-5 text-teal-600" />
          <span className="text-sm font-medium text-slate-600">Financiamiento recibido</span>
        </div>
        <p className="text-2xl font-bold text-slate-800">{formatCurrency(balance.financiamiento)}</p>
      </div>
    )}

    <div className={`rounded-xl shadow-md border p-6 ${
      balance.balance >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
    }`}>
      <div className="flex items-center gap-2 mb-2">
        <DollarSign className={`w-5 h-5 ${balance.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`} />
        <span className="text-sm font-medium text-slate-600">Balance {etiqueta}</span>
      </div>
      <p className={`text-2xl font-bold ${balance.balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
        {formatCurrency(balance.balance)}
      </p>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Tarjeta de deuda pendiente (modo propio) — separada del balance del período:
// la deuda es un total vigente, no algo que deba ocultarse si el mes no tuvo movimientos.
// ---------------------------------------------------------------------------

interface DeudaPendienteCardProps {
  readonly monto: number;
}

const DeudaPendienteCard: React.FC<DeudaPendienteCardProps> = ({ monto }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex items-center justify-between">
    <div className="flex items-center gap-2">
      <TrendingDown className="w-5 h-5 text-violet-600" />
      <span className="text-sm font-medium text-slate-600">Deuda pendiente</span>
    </div>
    <span className="text-lg font-bold text-slate-800">{formatCurrency(monto)}</span>
  </div>
);

// ---------------------------------------------------------------------------
// Tabla agrupada (modo agrupado)
// ---------------------------------------------------------------------------

interface TablaAgrupadaProps {
  readonly entidades: BalanceEntidad[];
  readonly tituloColumna: string;
}

const TablaAgrupada: React.FC<TablaAgrupadaProps> = ({ entidades, tituloColumna }) => {
  const totalIngresos = entidades.reduce((s, e) => s + e.ingresos, 0);
  const totalEgresos = entidades.reduce((s, e) => s + e.egresos, 0);
  const totalFinanciamiento = entidades.reduce((s, e) => s + e.financiamiento, 0);
  // El balance de cada entidad ya incluye su financiamiento: se suma tal cual, no se recalcula.
  const totalBalance = entidades.reduce((s, e) => s + e.balance, 0);

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h3 className="text-xl font-bold text-slate-800">{tituloColumna}</h3>
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span className="text-emerald-600 font-semibold">
            Ingresos: {formatCurrency(totalIngresos)}
          </span>
          <span className="text-red-600 font-semibold">
            Egresos: {formatCurrency(totalEgresos)}
          </span>
          {totalFinanciamiento > 0 && (
            <span className="text-teal-700 font-semibold">
              Financiamiento: {formatCurrency(totalFinanciamiento)}
            </span>
          )}
          <span className={`font-bold ${totalBalance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            Balance: {formatCurrency(totalBalance)}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                {tituloColumna}
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Ingresos
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Egresos
              </th>
              <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                Balance
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {entidades.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-slate-500">
                  No hay datos para el período seleccionado
                </td>
              </tr>
            ) : (
              entidades.map((entidad) => (
                <tr key={entidad.entidad_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 text-sm font-medium text-slate-800">
                    {entidad.entidad_nombre}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-emerald-600 font-semibold">
                    {formatCurrency(entidad.ingresos)}
                  </td>
                  <td className="px-6 py-4 text-sm text-right text-red-600 font-semibold">
                    {formatCurrency(entidad.egresos)}
                  </td>
                  <td className={`px-6 py-4 text-sm text-right font-bold ${
                    entidad.balance >= 0 ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    {formatCurrency(entidad.balance)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Estado vacío
// ---------------------------------------------------------------------------

interface EstadoVacioProps {
  readonly etiqueta: string;
  readonly mes: number;
  readonly anio: number;
}

const EstadoVacio: React.FC<EstadoVacioProps> = ({ etiqueta, mes, anio }) => (
  <div className="bg-white rounded-xl shadow-md border border-slate-200 p-12 text-center">
    <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-4" />
    <p className="text-slate-500 text-lg font-medium">
      La {etiqueta} no tiene movimientos en este período
    </p>
    <p className="text-slate-400 text-sm mt-1">
      {MESES.find(m => m.value === mes)?.label} {anio}
    </p>
  </div>
);

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

function FondosEntidad({ ownerContext, mes, año }: {
  readonly ownerContext: OwnerContext;
  readonly mes: number;
  readonly año: number;
}) {
  const { usuario } = useAuth();
  if (ownerContext.tipo === 'global') return null;
  const owner = { ...ownerContext, tipo: ownerContext.tipo };
  const autorizado = puedeOperarDinero(usuario, { ...owner, diocesisId: null, provinciaEclesiasticaId: null });
  return <FondosCampanaTabla ownerContext={owner} puedeOperar={autorizado}
    periodo={{ tipo: 'mensual', mes, año }} />;
}

export const BalanceJAView: React.FC<BalanceJAViewProps> = ({
  ownerContext,
  modo = 'propio',
  initialMes,
  initialAño,
}) => {
  const [mes, setMes] = useState(initialMes ?? new Date().getMonth() + 1);
  const [anio, setAnio] = useState(initialAño ?? new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [balancePropio, setBalancePropio] = useState<BalanceResumen | null>(null);
  const [deudaPendiente, setDeudaPendiente] = useState(0);
  const [entidades, setEntidades] = useState<BalanceEntidad[]>([]);
  const [notificacion, setNotificacion] = useState<NotificacionBalance | null | undefined>(undefined);
  const [showModal, setShowModal] = useState(false);

  const años = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const esAgrupado = modo === 'agrupado' || ownerContext.tipo === 'global';
  const esJADPropio = !esAgrupado && ownerContext.tipo === 'diocesis';
  const etiquetaJA = resolverEtiquetaJA(ownerContext.tipo);
  const tituloColumna = resolverTituloColumna(ownerContext.tipo);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (esAgrupado) {
        await cargarModoAgrupado(ownerContext, mes, anio, setEntidades);
      } else {
        await cargarModoPropio(ownerContext, mes, anio, setBalancePropio);
      }
      if (esJADPropio) {
        const notif = await notificacionBalanceService.getByEntidadYPeriodo(ownerContext.id, mes, anio);
        setNotificacion(notif);
      }
    } catch (err) {
      // Sin esto la pantalla afirmaba "no tiene movimientos en este período", que es mentira:
      // no es que no haya, es que no se pudieron leer.
      setError('No pudimos cargar el balance de este período.');
      reportarErrorInesperado(err, 'No pudimos cargar el balance de este período.');
    } finally {
      setLoading(false);
    }

    // Independiente del balance/notificación: una falla acá no debe impedir que
    // el resto del período se muestre (no acoplar un failure nuevo al bloque existente).
    if (!esAgrupado) {
      try {
        const saldo = await deudaService.getSaldoPendienteEntidad(ownerContext);
        setDeudaPendiente(saldo);
      } catch {
        // error silencioso, misma convención del bloque anterior
      }
    }
  }, [esAgrupado, esJADPropio, ownerContext, mes, anio]);

  useEffect(() => {
    void cargarDatos();
  }, [cargarDatos]);

  const hayMovimientos = esAgrupado
    ? entidades.some(e => e.ingresos > 0 || e.egresos > 0 || e.financiamiento > 0)
    : balancePropio !== null && (balancePropio.ingresos > 0 || balancePropio.egresos > 0 || balancePropio.financiamiento > 0);

  const contenidoCargando = esAgrupado ? <SkeletonTabla /> : <SkeletonCards />;

  const contenidoCargado = renderContenidoCargado({
    hayMovimientos,
    esAgrupado,
    etiquetaJA,
    mes,
    anio,
    entidades,
    tituloColumna,
    balancePropio,
  });

  const modeModal = notificacion?.estado === 'observado' ? 'reenviar' : 'enviar';

  return (
    <div className="space-y-6">
      {/* Selector de período y botón de notificación */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
          <PillSelect
            id="ja-mes-select"
            value={mes.toString()}
            onChange={(v) => setMes(Number(v))}
            options={MESES.map(m => ({ value: m.value.toString(), label: m.label }))}
          />
          <PillSelect
            id="ja-anio-select"
            value={anio.toString()}
            onChange={(v) => setAnio(Number(v))}
            options={años.map(a => ({ value: a.toString(), label: a.toString() }))}
          />
        </div>

        {esJADPropio && !loading && renderBotonNotificacion(notificacion, () => setShowModal(true))}
      </div>

      <FormErrorAlert
        error={error}
        onClear={() => { setError(null); }}
        action={{ label: 'Reintentar', onClick: () => { void cargarDatos(); } }}
      />

      {/* Con la carga fallida no hay datos porque no se pudieron leer: el aviso de arriba ya lo
          dice, y mostrar "no tiene movimientos en este período" sería afirmar algo falso. */}
      {loading && contenidoCargando}
      {!loading && !error && contenidoCargado}

      {!esAgrupado && !loading && <DeudaPendienteCard monto={deudaPendiente} />}
      {!esAgrupado && <FondosEntidad ownerContext={ownerContext} mes={mes} año={anio} />}

      {!esAgrupado && (
        <CuotaIglesiaDesglose ownerContext={ownerContext} mes={mes} anio={anio} />
      )}

      {esJADPropio && (
        <NotificarBalanceModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); cargarDatos(); }}
          entidadId={ownerContext.id}
          tipoEntidad="diocesis"
          mes={mes}
          año={anio}
          mode={modeModal}
          notificacionId={notificacion?.id}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Desglose cuota iglesia
// ---------------------------------------------------------------------------

interface DesgloseJADRow { lumisial_id: string; lumisial_nombre: string; monto: number; montoJAL: number; montoPA: number }
interface DesgloseJANRow { diocesis_id: string; diocesis_nombre: string; monto: number; n_lumisiales: number }
interface DesgloseJANLumisialRow { lumisial_id: string; lumisial_nombre: string; monto: number; montoJAL: number; montoPA: number }

interface CuotaIglesiaDesgloseProps {
  readonly ownerContext: OwnerContext;
  readonly mes: number;
  readonly anio: number;
}

const SkeletonSubRow: React.FC = () => (
  <tr className="bg-slate-50 animate-pulse" aria-hidden="true" tabIndex={-1}>
    <td className="px-10 py-3"><div className="h-3 bg-slate-200 rounded w-36" /></td>
    <td className="px-6 py-3 text-right"><div className="h-3 bg-slate-200 rounded w-20 ml-auto" /></td>
  </tr>
);

interface TablaJADProps {
  readonly rows: DesgloseJADRow[];
  readonly total: number;
}
const TablaJAD: React.FC<TablaJADProps> = ({ rows, total }) => (
  <table className="w-full">
    <thead className="bg-slate-50">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Lumisial</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Monto</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-slate-100">
      {rows.length === 0 ? (
        <tr><td colSpan={2} className="px-6 py-6 text-center text-slate-500 text-sm">Sin cuota iglesia registrada este período</td></tr>
      ) : rows.map(r => (
        <>
          <tr key={r.lumisial_id} className="hover:bg-slate-50">
            <td className="px-6 py-3 text-sm font-medium text-slate-800">{r.lumisial_nombre}</td>
            <td className="px-6 py-3 text-sm text-right font-semibold text-emerald-700">{formatCurrency(r.monto)}</td>
          </tr>
          {r.montoJAL > 0 && r.montoPA > 0 && (
            <>
              <tr key={`${r.lumisial_id}-jal`} className="bg-slate-50">
                <td className="pl-10 pr-6 py-1.5 text-xs text-slate-500">Transferencia JAL</td>
                <td className="px-6 py-1.5 text-xs text-right text-slate-500">{formatCurrency(r.montoJAL)}</td>
              </tr>
              <tr key={`${r.lumisial_id}-pa`} className="bg-slate-50">
                <td className="pl-10 pr-6 py-1.5 text-xs text-slate-500">Pago automático</td>
                <td className="px-6 py-1.5 text-xs text-right text-slate-500">{formatCurrency(r.montoPA)}</td>
              </tr>
            </>
          )}
        </>
      ))}
      {rows.length > 0 && (
        <tr className="bg-slate-50 font-bold border-t border-slate-200">
          <td className="px-6 py-3 text-sm text-slate-800">Total</td>
          <td className="px-6 py-3 text-sm text-right text-emerald-800">{formatCurrency(total)}</td>
        </tr>
      )}
    </tbody>
  </table>
);

interface TablaJANProps {
  readonly rows: DesgloseJANRow[];
  readonly total: number;
  readonly expandedDiocesis: Set<string>;
  readonly loadingDiocesis: Record<string, boolean>;
  readonly lumisialesPorDiocesis: Record<string, DesgloseJANLumisialRow[]>;
  readonly onToggleDiocesis: (id: string) => void;
}
const TablaJAN: React.FC<TablaJANProps> = ({ rows, total, expandedDiocesis, loadingDiocesis, lumisialesPorDiocesis, onToggleDiocesis }) => (
  <table className="w-full">
    <thead className="bg-slate-50">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Diócesis</th>
        <th className="px-6 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Lumisiales</th>
        <th className="px-6 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Monto</th>
      </tr>
    </thead>
    <tbody className="divide-y divide-slate-100">
      {rows.length === 0 ? (
        <tr><td colSpan={3} className="px-6 py-6 text-center text-slate-500 text-sm">Sin cuota iglesia registrada este período</td></tr>
      ) : rows.map(r => (
        <>
          <tr
            key={r.diocesis_id}
            className="hover:bg-slate-50 cursor-pointer"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onToggleDiocesis(r.diocesis_id); }}
            onClick={() => onToggleDiocesis(r.diocesis_id)}
          >
            <td className="px-6 py-3 text-sm text-slate-800">
              <div className="flex items-center gap-2">
                {expandedDiocesis.has(r.diocesis_id)
                  ? <ChevronDown className="w-4 h-4 text-slate-400" />
                  : <ChevronRight className="w-4 h-4 text-slate-400" />
                }
                {r.diocesis_nombre}
              </div>
            </td>
            <td className="px-6 py-3 text-sm text-center text-slate-600">{r.n_lumisiales}</td>
            <td className="px-6 py-3 text-sm text-right font-semibold text-emerald-700">{formatCurrency(r.monto)}</td>
          </tr>
          {expandedDiocesis.has(r.diocesis_id) && (
            loadingDiocesis[r.diocesis_id]
              ? <><SkeletonSubRow /><SkeletonSubRow /></>
              : (lumisialesPorDiocesis[r.diocesis_id] ?? []).map(l => (
                  <>
                    <tr key={l.lumisial_id} className="bg-slate-50">
                      <td className="px-10 py-3 text-sm text-slate-700">{l.lumisial_nombre}</td>
                      <td className="px-6 py-3 text-sm text-center text-slate-300">—</td>
                      <td className="px-6 py-3 text-sm text-right text-emerald-600">{formatCurrency(l.monto)}</td>
                    </tr>
                    {l.montoJAL > 0 && l.montoPA > 0 && (
                      <>
                        <tr key={`${l.lumisial_id}-jal`} className="bg-slate-50">
                          <td className="pl-14 pr-6 py-1.5 text-xs text-slate-500">Transferencia JAL</td>
                          <td className="px-6 py-1.5 text-xs text-center text-slate-300">—</td>
                          <td className="px-6 py-1.5 text-xs text-right text-slate-500">{formatCurrency(l.montoJAL)}</td>
                        </tr>
                        <tr key={`${l.lumisial_id}-pa`} className="bg-slate-50">
                          <td className="pl-14 pr-6 py-1.5 text-xs text-slate-500">Pago automático</td>
                          <td className="px-6 py-1.5 text-xs text-center text-slate-300">—</td>
                          <td className="px-6 py-1.5 text-xs text-right text-slate-500">{formatCurrency(l.montoPA)}</td>
                        </tr>
                      </>
                    )}
                  </>
                ))
          )}
        </>
      ))}
      {rows.length > 0 && (
        <tr className="bg-slate-50 font-bold">
          <td className="px-6 py-3 text-sm text-slate-800">Total</td>
          <td className="px-6 py-3 text-sm text-center text-slate-300">—</td>
          <td className="px-6 py-3 text-sm text-right text-emerald-800">{formatCurrency(total)}</td>
        </tr>
      )}
    </tbody>
  </table>
);

const CuotaIglesiaDesglose: React.FC<CuotaIglesiaDesgloseProps> = ({ ownerContext, mes, anio }) => {
  const [abierto, setAbierto] = useState(false);
  const [fueAbierto, setFueAbierto] = useState(false);
  const [rowsJAD, setRowsJAD] = useState<DesgloseJADRow[]>([]);
  const [rowsJAN, setRowsJAN] = useState<DesgloseJANRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedPeriod, setLoadedPeriod] = useState('');
  const { expanded: expandedDiocesis, toggle: toggleDiocesis } = useToggleSet();
  const [lumisialesPorDiocesis, setLumisialesPorDiocesis] = useState<Record<string, DesgloseJANLumisialRow[]>>({});
  const [loadingDiocesis, setLoadingDiocesis] = useState<Record<string, boolean>>({});

  const period = `${ownerContext.id}-${mes}-${anio}`;

  const cargar = useCallback(async () => {
    if (loadedPeriod === period) return;
    setLoading(true);
    try {
      if (ownerContext.tipo === 'diocesis') {
        const data = await balanceConsolidadoService.getCuotaIglesiaDesgloseJAD(ownerContext.id, mes, anio);
        setRowsJAD(data);
      } else if (ownerContext.tipo === 'provincia') {
        const data = await balanceConsolidadoService.getCuotaIglesiaDesgloseJANPorDiocesis(ownerContext.id, mes, anio);
        setRowsJAN(data);
      }
      setLoadedPeriod(period);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [ownerContext, mes, anio, period, loadedPeriod]);

  // Al cambiar el período, limpiar caché y recargar si el acordeón está abierto
  useEffect(() => {
    setLoadedPeriod('');
    setLumisialesPorDiocesis({});
  }, [mes, anio, ownerContext.id]);

  useEffect(() => {
    if (abierto) cargar();
  }, [abierto, cargar]);

  const handleToggle = () => {
    const siguienteAbierto = !abierto;
    setAbierto(siguienteAbierto);
    if (siguienteAbierto) setFueAbierto(true);
  };

  const handleToggleDiocesis = (diocesisId: string) => {
    toggleDiocesis(diocesisId);
    const wasExpanded = expandedDiocesis.has(diocesisId);
    if (!wasExpanded && !lumisialesPorDiocesis[diocesisId]) {
      setLoadingDiocesis(prev => ({ ...prev, [diocesisId]: true }));
      balanceConsolidadoService.getCuotaIglesiaDesgloseJANLumisiales(diocesisId, mes, anio)
        .then(data => setLumisialesPorDiocesis(prev => ({ ...prev, [diocesisId]: data })))
        .catch(() => { /* silencioso */ })
        .finally(() => setLoadingDiocesis(prev => ({ ...prev, [diocesisId]: false })));
    }
  };

  const isJAD = ownerContext.tipo === 'diocesis';
  const isJAN = ownerContext.tipo === 'provincia';
  if (!isJAD && !isJAN) return null;

  const totalJAD = rowsJAD.reduce((s, r) => s + r.monto, 0);
  const totalJAN = rowsJAN.reduce((s, r) => s + r.monto, 0);
  const total = isJAD ? totalJAD : totalJAN;

  if (!fueAbierto && total === 0 && loadedPeriod === period) return null;

  const contenidoCargado = isJAD
    ? <TablaJAD rows={rowsJAD} total={totalJAD} />
    : <TablaJAN rows={rowsJAN} total={totalJAN} expandedDiocesis={expandedDiocesis} loadingDiocesis={loadingDiocesis} lumisialesPorDiocesis={lumisialesPorDiocesis} onToggleDiocesis={handleToggleDiocesis} />;
  const contenidoAbierto = loading
    ? <div className="p-6 space-y-2">{[1, 2].map(i => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
    : contenidoCargado;

  return (
    <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
      >
        <span className="text-base font-semibold text-slate-800">
          Cuota iglesia recibida{loadedPeriod === period ? ` — ${formatCurrency(total)}` : ''}
        </span>
        {abierto
          ? <ChevronDown className="w-5 h-5 text-slate-500" />
          : <ChevronRight className="w-5 h-5 text-slate-500" />
        }
      </button>
      {abierto && <div className="border-t border-slate-200">{contenidoAbierto}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Botón de notificación JAD → JAN
// ---------------------------------------------------------------------------

function renderBotonNotificacion(
  notificacion: NotificacionBalance | null | undefined,
  onAbrir: () => void
): React.ReactElement | null {
  // Aún cargando
  if (notificacion === undefined) return null;

  // Ya enviado y recibido
  if (notificacion?.estado === 'recibido') {
    return (
      <div className="flex items-center gap-1.5 text-sm text-emerald-700 font-medium">
        <CheckCircle className="w-4 h-4" />
        Balance recibido por JAN
      </div>
    );
  }

  // Ya enviado (pendiente de revisión)
  if (notificacion?.estado === 'enviado') {
    return (
      <div className="flex items-center gap-1.5 text-sm text-blue-600 font-medium">
        <Send className="w-4 h-4" />
        Balance enviado a JAN
      </div>
    );
  }

  // Observado — se puede reenviar
  if (notificacion?.estado === 'observado') {
    return (
      <button
        type="button"
        onClick={onAbrir}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
      >
        <AlertCircle className="w-3.5 h-3.5" />
        Reenviar balance corregido
      </button>
    );
  }

  // No enviado aún
  return (
    <button
      type="button"
      onClick={onAbrir}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
    >
      <Send className="w-3.5 h-3.5" />
      Enviar balance a JAN
    </button>
  );
}

// ---------------------------------------------------------------------------
// Helpers de carga (extraídos para reducir complejidad cognitiva)
// ---------------------------------------------------------------------------

async function cargarModoPropio(
  ownerContext: OwnerContext,
  mes: number,
  anio: number,
  setBalancePropio: (b: BalanceResumen) => void
): Promise<void> {
  if (ownerContext.tipo === 'diocesis') {
    const data = await balanceConsolidadoService.getBalanceJAD(ownerContext.id, mes, anio);
    setBalancePropio(data);
  } else if (ownerContext.tipo === 'provincia') {
    const data = await balanceConsolidadoService.getBalanceJAN(ownerContext.id, mes, anio);
    setBalancePropio(data);
  }
}

async function cargarModoAgrupado(
  ownerContext: OwnerContext,
  mes: number,
  anio: number,
  setEntidades: (e: BalanceEntidad[]) => void
): Promise<void> {
  if (ownerContext.tipo === 'provincia') {
    const data = await balanceConsolidadoService.getBalanceJADsPorProvincia(ownerContext.id, mes, anio);
    setEntidades(data);
  } else {
    // tipo === 'global'
    const data = await balanceConsolidadoService.getBalanceJANs(mes, anio);
    setEntidades(data);
  }
}

// ---------------------------------------------------------------------------
// Helper de render del contenido cargado (extrae ternarios anidados)
// ---------------------------------------------------------------------------

interface RenderContenidoParams {
  readonly hayMovimientos: boolean;
  readonly esAgrupado: boolean;
  readonly etiquetaJA: string;
  readonly mes: number;
  readonly anio: number;
  readonly entidades: BalanceEntidad[];
  readonly tituloColumna: string;
  readonly balancePropio: BalanceResumen | null;
}

function renderContenidoCargado(p: RenderContenidoParams): React.ReactElement | null {
  if (!p.hayMovimientos) {
    return <EstadoVacio etiqueta={p.etiquetaJA} mes={p.mes} anio={p.anio} />;
  }
  if (p.esAgrupado) {
    return <TablaAgrupada entidades={p.entidades} tituloColumna={p.tituloColumna} />;
  }
  if (p.balancePropio !== null) {
    return <ResumenCards balance={p.balancePropio} etiqueta={p.etiquetaJA} />;
  }
  return null;
}
